<template>
    <div class="row mt-6">
        <div class="col-12 flex-column align-start">
            <div class="order-attributes mt-3">
                <div class="flex-column half-width">
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('Item') }}</div>
                        <div>{{ activeItemIndex+1 }}/{{ orders.length }}</div>
                    </div>
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('SO number') }}</div>
                        <div>{{ activeItem.sales_order_number || '' }}</div>
                    </div>
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('Article') }}</div>
                        <div>{{ activeItem.article || '' }}</div>
                    </div>
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('Model number') }}</div>
                        <div>{{ activeItem.model_number || '' }}</div>
                    </div>
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('Ordered quantity') }}</div>
                        <div>{{ activeItem.ordered_quantity || '' }}</div>
                    </div>
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('Customer') }}</div>
                        <div>{{ activeItem.customer || '' }}</div>
                    </div>
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('Customer order number') }}</div>
                        <div>{{ activeItem.customer_order_number || '' }}</div>
                    </div>
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('Project') }}</div>
                        <div>{{ activeItem.project || '' }}</div>
                    </div>
                </div>
                <div class="flex-column half-width">
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('Order delivered on') }}</div>
                        <div>{{ activeItem.order_delivery_date || '' }}</div>
                    </div>
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('Warranty') }}</div>
                        <div>{{ activeItem.warranty || '' }}</div>
                    </div>
                    <div class="flex-between align-start">
                        <div class="bold">{{ $t('Valid warranty') }}</div>
                        <div>{{ activeItem.valid_warranty || '' }}</div>
                    </div>
                </div>
            </div>
            <ValidationObserver class="w-100" v-slot="{ validate }" ref="observer">
                <form
                    class="form mt-5 mb-5 order-attributes"
                    @submit.prevent="validate(submitForm())"
                >
                    <div class="flex-column half-width align-start">
                        <div class="form-group">
                            <ValidationProvider
                                v-slot="{ errors }"
                                :rules="{ required: true, numeric: true, maxOrdered: activeItem.ordered_quantity, manualQuantityCheck: isQuantityInvalid }"
                            >
                                <SfInput
                                    :valid="!errors[0]"
                                    :error-message="$t(errors[0])"
                                    v-model="mainFormFields.quantity"
                                    required
                                    name="quantity"
                                    :label="$t('Number of products with issues')"
                                    class="form__element"
                                    @focus="oldQuantity = mainFormFields.quantity"
                                    @blur="saveMainForm()"
                                />
                            </ValidationProvider>
                        </div>
                        <div class="form-group mt-3 relative-pos">
                            <ValidationProvider
                                v-slot="{ errors }"
                                rules="required"
                            >
                                <div class="attribute-tooltip flex">
                                    <svg-image
                                        class="icon hover-icon"
                                        icon="info"
                                        height="24"
                                        width="24"
                                    />
                                    <span class="attribute-tooltiptext" v-html="attributeInfo['preferredCourseOfActionTooltip']"></span>
                                </div>
                                <v-select
                                    :clearable="false"
                                    label="label"
                                    key="label"
                                    :options="courseOfActions"
                                    :placeholder="$t('Preferred course of action')"
                                    v-model="mainFormFields.preferredCourseOfAction"
                                    @input="saveMainForm()"
                                />
                                <p v-if="errors[0]" class="sf-input__error-message">
                                    {{ $t(errors[0]) }}
                                </p>
                            </ValidationProvider>
                        </div>
                    </div>
                </form>
            </ValidationObserver>
            <div class="flex-between w-100">
                <div>
                    <h4>{{ $t('Product issues') }}</h4>
                </div>
                <div v-if="displayIssuesWarning" class="warning-message">
                    {{ $t('Please add at least one issue to proceed') }}
                </div>
            </div>
            <div class="descrption mb-3 w-100">
                {{ $t('You can add multiple issues, and different quantity can be specified for each issue, but it cannot exceed claim quantity entered above.') }}
            </div>
            <vue-good-table
                ref="issueList"
                :columns="columnsData"
                :rows="items"
            >
                <template slot="table-column" slot-scope="props">
                    <span>
                        {{ props.column.label }}
                    </span>
                </template>
                <template slot="table-row" slot-scope="props">
                    <span v-if="props.column.field !== 'actions'">
                        {{
                            isDateField(props.column.field)
                                ? formatDate(props.formattedRow[props.column.field])
                                : (props.column.field === 'issue_category'
                                    ? props.formattedRow.issue_category.name
                                    : props.formattedRow[props.column.field])
                        }}
                    </span>
                    <div v-else class="flex flex-align-center">
                        <div @click="removeItem(props.row.originalIndex)" class="pointer mr-3">
                            <svg-image class="icon" icon="close" height="16" width="16" />
                        </div>
                        <div @click="editItem(props.row, props.row.originalIndex)" class="pointer">
                            <svg-image class="icon" icon="edit" height="18" width="18" />
                        </div>
                    </div>
                </template>
            </vue-good-table>
            <div
                class="button medium button-primary mt-5"
                @click="openModal"
            >
                    {{ $t('Add') }}
            </div>
        </div>
        <SfModal
            :visible="isModalOpen"
            class="project-modal"
            @close="isModalOpen = false; clearIssueForm(false)"
        >
            <div class="container">
                <ValidationObserver v-slot="{ handleSubmit }" key="addIssue" ref="issueObserver">
                    <form class="form" @submit.prevent="handleSubmit(addIssue)" enctype="multipart/form-data">
                        <div class="row mt-5">
                            <div class="col-12">
                                <div class="form-group">
                                    <ValidationProvider
                                        rules="required"
                                        v-slot="{ errors }"
                                    >
                                        <v-select
                                            :clearable="false"
                                            label="name"
                                            key="id"
                                            required
                                            :placeholder="$t('Issue category')"
                                            :options="issuesCategories"
                                            v-model="issueFormFields.issue_category"
                                           @input="warningMessage = ''"
                                        />
                                        <p v-if="errors[0]" class="sf-input__error-message">
                                            {{ $t(errors[0]) }}
                                        </p>
                                    </ValidationProvider>

                                </div>
                            </div>
                        </div>
                        <div class="row mt-5">
                            <div class="col-12">
                                <div class="form-group">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ required: true, numeric: true, maxClaimQuantity: mainFormFields.quantity }"
                                    >
                                        <SfInput
                                            :valid="!errors[0]"
                                            :error-message="$t(errors[0])"
                                            v-model="issueFormFields.quantity"
                                            required
                                            name="quantity"
                                            :label="$t('Number of products with this issue')"
                                            class="form__element text-italic"
                                            autocomplete="off"
                                        />
                                    </ValidationProvider>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12">
                                <div class="form-group">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        rules="required|min:3"
                                    >
                                        <SfInput
                                            :valid="!errors[0]"
                                            :error-message="$t(errors[0])"
                                            v-model="issueFormFields.end_user"
                                            required
                                            name="end_user"
                                            :label="$t('End user')"
                                            class="form__element"
                                            autocomplete="off"
                                        />
                                    </ValidationProvider>
                                </div>
                            </div>
                        </div>
                        <div class="row mt-4">
                            <div class="col-6">
                                <div class="form-group">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        rules="required|pastDate"
                                    >
                                        <vue-datepicker
                                            :format="'DD-MM-YYYY'"
                                            :required="true"
                                            class="form__element"
                                            :label="$t('Installation date')"
                                            v-model="issueFormFields.installation_date"
                                            :disabled-date="disableInstallationAfterFailure"
                                        />
                                        <p v-if="errors[0]" class="sf-input__error-message">
                                            {{ $t(errors[0]) }}
                                        </p>
                                    </ValidationProvider>
                                </div>
                            </div>
                            <div class="col-6">
                                <div class="form-group">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        rules="required|pastDate"
                                    >
                                        <vue-datepicker
                                            :format="'DD-MM-YYYY'"
                                            :required="true"
                                            class="form__element"
                                            :label="$t('Failure date')"
                                            v-model="issueFormFields.failure_date"
                                            :disabled-date="disableFailureBeforeInstallation"
                                        />
                                        <p v-if="errors[0]" class="sf-input__error-message">
                                            {{ $t(errors[0]) }}
                                        </p>
                                    </ValidationProvider>
                                </div>
                            </div>
                        </div>
                        <div class="row mt-5">
                            <div class="col-12">
                                <div class="form-group">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ checkRequired: issueFormFields.issue_category }"
                                        name="description"
                                    >
                                        <SfTextarea
                                            :valid="!errors[0]"
                                            :error-message="$t(errors[0])"
                                            v-model="issueFormFields.description"
                                            name="description"
                                            :label="$t('Description')"
                                            class="form__element"
                                            rows="4"
                                            autocomplete="off"
                                        />
                                    </ValidationProvider>
                                </div>
                            </div>
                        </div>
                        <div class="row mt-4">
                            <div class="col-12">
                                <div class="form-group image-upload">
                                    <ValidationProvider v-slot="{ errors }">
                                        <div class="attribute-tooltip flex">
                                            <svg-image
                                                class="icon hover-icon"
                                                icon="info"
                                                height="24"
                                                width="24"
                                            />
                                            <span class="attribute-tooltiptext" v-html="attributeInfo['pictures']"></span>
                                        </div>
                                        <label class="file-upload-label" for="upload-input"
                                            @dragover.prevent="onDragOver"
                                            @dragleave.prevent="onDragLeave"
                                            @drop.prevent="onDrop"
                                            :class="{ 'drag-over': isDragOver }">
                                            <span class="upload-text">
                                                <svg-image class="icon hover-icon" icon="upload" height="24" width="24" />
                                                {{ $t("Upload images") }}
                                            </span>
                                        </label>
                                        <input
                                            id="upload-input"
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            class="hidden"
                                            @change="handleImageUpload"
                                        />
                                        <p v-if="errors[0]" class="sf-input__error-message">
                                            {{ $t(errors[0]) }}
                                        </p>
                                        <p v-if="warningMessage" class="warning-message">
                                            {{ warningMessage }}
                                        </p>
                                        <div v-if="uploadedImages.length" class="image-preview">
                                            <ul v-if="uploadedImages.length" class="file-list">
                                                <li v-for="(image, index) in uploadedImages" :key="index" class="file-item">
                                                    <span class="file-name" :title="image.fileName">
                                                        {{ truncateFileName(image.fileName, 34) }}
                                                    </span>
                                                    <div @click="removeImage(index)" class="pointer">
                                                        <svg-image class="icon" icon="close" height="16" width="16" />
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </ValidationProvider>
                                </div>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            class="mt-4 button medium button-secondary"
                        >
                            {{ isEditing ? $t('Update') : $t('Add') }}
                        </Button>
                    </form>
                </ValidationObserver>
            </div>
        </SfModal>
    </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table';
import {
    defineComponent,
    computed,
    ref,
    watch,
    onMounted,
    useContext,
} from '@nuxtjs/composition-api';
import { SfInput, SfModal, SfLoader, SfTextarea, SfNotification } from '@storefront-ui/vue';
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { required, numeric, min } from "vee-validate/dist/rules";
import { useClaimSubmission } from '../index';
import VueDatepicker from '~/components/Common/Calendar/VueDatepicker.vue';
import { format } from 'date-fns';

extend('required', {
	...required,
	message: 'This field is required',
});

extend('numeric', {
  ...numeric,
  message: 'This field must be a number',
});

extend('min', {
  ...min,
  message: 'This field have more than 3 characters',
});

extend('pastDate', {
    validate(value) {
        // Ensure `value` is a valid Date object
        const selectedDate = value instanceof Date ? value : new Date(value);
        if (isNaN(selectedDate)) return false; // Invalid date handling

        const today = new Date();

        return selectedDate <= today; // Ensures the selected date is in the past
    },
    message: 'The date must be in the past.',
});

extend('maxOrdered', {
    params: ['max'],
    validate(value, { max }) {
        return Number(value) <= Number(max);
    },
    message: 'Claim quantity cannot exceed ordered quantity.'
});

extend('maxClaimQuantity', {
    params: ['max'],
    validate(value, { max }) {
        return Number(value) <= Number(max);
    },
    message: 'Issues quantity cannot exceed claim quantity.'
});

extend('manualQuantityCheck', {
    validate: () => false,
    message: 'Product issues can not have item with higher quantity.'
});

extend('checkRequired', {
    params: ['category'],
    validate(value, { category }) {
        if (!category || (category && !category.description_required)) return true;

        return !!value && value.trim().length >= 3;
    },
    message: (_, values) => {
        if (!values.category) return 'Description is required.';
        return `Description is required for selected category.`;
    }
});

export default defineComponent({
    name: 'Step2',
    components: {
		SfLoader,
        SfInput,
        SfModal,
        SfTextarea,
        SfNotification,
        ValidationProvider,
        ValidationObserver,
        VueGoodTable,
        VueDatepicker
	},
    props: {
        activeItemIndex: Number,
        orders: Array,
        displayIssuesWarning: Boolean,
        mainFormFields: Object,
	},
    emits: ['updateIssues','removeIssue', 'saveMainForm'],
    setup(props, { emit }) {
        const { app } = useContext();
        const apiState = app.$vsf.$magento.config.state;
        const activeItem = computed(() => props.orders[props.activeItemIndex] ?? {});
        const currentItems = computed(() => props.orders[props.activeItemIndex].issues ?? []);
        const isModalOpen = ref(false);
        const uploadedImages = ref([]);
        const isDragOver = ref(false);
        const { fetchDropdownOptions, dropdownOptions, uploadImages } = useClaimSubmission();
        const warningMessage = ref('');
        const isEditing = ref(false);
        const currentEditIndex = ref(null);
        const MAX_SIZE_MB = 5;
        const observer = ref(null);
        const issueObserver = ref(null);
        const isQuantityInvalid = ref(false);
        const oldQuantity = ref(0);

        const attributeInfo = {
            pictures: `
                <p>Please attach:</p>
                <ul>
                    <li>Label photo with matching sales order number, article, and model number</li>
                    <li>Photo of visual defect when applicable</li>
                </ul>
            `,
            preferredCourseOfActionTooltip: "You can select one of the options below to indicacte how you would like to proceed. We will take this into account and either act accordingly or contact you for more details as soon as possible. Please note that by choosing option 2 and 3, components will be invoiced to you and credited only when they are fully returned for diagnostics and a test report is created concluding amnufacturer's fault.",
        };

        const columnsData = ref([
            { label: 'Issue category', field: 'issue_category' },
            { label: 'Quantity', field: 'quantity' },
            { label: 'End user', field: 'end_user' },
            { label: 'Installation date', field: 'installation_date' },
            { label: 'Failure date', field: 'failure_date' },
            { label: 'Description', field: 'description' },
            { label: 'Actions', field: 'actions' }
        ]);

        const issuesCategories = computed(() => dropdownOptions.value?.issue_categories ?? []);
        const courseOfActions = computed(() => dropdownOptions.value?.preferred_actions ?? []);
        const items = ref([]);

        const issueFormFields = ref({
            issue_category: '',
            quantity: '',
            end_user: '',
            installation_date: '',
            failure_date: '',
            description: '',
            pictures: '',
        });

        const addIssue = async() => {
            if (issueFormFields.value.description === ' ') {
                issueFormFields.value.description = '';
            }

            if (issueFormFields.value.issue_category.description_required && issueFormFields.value.description === '') {
                issueFormFields.value.description = ' ';

                return;
            }


            if (issueFormFields.value.issue_category.photos_required && uploadedImages.value.length === 0) {
                warningMessage.value = `Images are required if category: ${issueFormFields.value.issue_category.name} is selected.`;

                return;
            }

            const newIssue = {
                issue_category: issueFormFields.value.issue_category,
                quantity: issueFormFields.value.quantity,
                end_user: issueFormFields.value.end_user,
                installation_date: issueFormFields.value.installation_date,
                failure_date: issueFormFields.value.failure_date,
                description: issueFormFields.value.description,
                pictures: [...uploadedImages.value],
            };

            if (isEditing.value) {
                // Update existing item by index
                items.value[currentEditIndex.value] = newIssue;
                emit('updateIssues', items.value);
            } else {
                // Add new item
                emit('updateIssues', [...items.value, newIssue]);
            }

            clearIssueForm(false);
            isModalOpen.value = false;
        }

        const editItem = (item, index) => {
            isEditing.value = true;
            currentEditIndex.value = index; // Store the index

            uploadedImages.value = [...item.pictures];
            issueFormFields.value = {
                ...item,
                pictures: uploadedImages.value,
            };

            isModalOpen.value = true;
        }

        const clearIssueForm = (fullClear) => {
            issueFormFields.value.issue_category = '';
            issueFormFields.value.quantity = '';
            issueFormFields.value.description = '';
            issueFormFields.value.pictures = '';

            if (fullClear) {
                issueFormFields.value.failure_date = '';
                issueFormFields.value.installation_date = '';
                issueFormFields.value.end_user = '';
            }

            uploadedImages.value = [];
            isEditing.value = false;
            currentEditIndex.value = null;
        }

        const removeItem = (index) => {
            emit('removeIssue', index);
        }

        const setCourseOfAction = (event) => {
            props.orders[props.activeItemIndex].preferredCourseOfAction = event || null;
        }

        const saveMainForm = () => {
            if (validateNotExceedMainQuantity()) {
                if (!validateIfIssuesDoesNotHaveHigherQuantity()) {
                    emit('saveMainForm', props.mainFormFields);
                } else {
                    props.mainFormFields.quantity = oldQuantity.value;
                }
            }
        };

        const handleImageUpload = async (event) => {
            const files = event.target?.files;
            if (!files) return;

            const validImages = [];

            Array.from(files).forEach(file => {
                if (!file.type.startsWith('image/')) {
                    warningMessage.value = 'Warning: Non-image files were added. Only image files are allowed.';
                    return;
                }

                if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                    warningMessage.value = `Warning: File "${file.name}" exceeds ${MAX_SIZE_MB}MB and was skipped.`;
                    return;
                }

                validImages.push(file);
            });

            if (validImages.length) {
                const uploaded = await uploadImages(validImages);
                uploadedImages.value.push(...uploaded);
                warningMessage.value = ''; // Clear if all ok
            }
        };

        const onDragOver = () => {
            isDragOver.value = true;
        };

        const onDragLeave = () => {
            isDragOver.value = false;
        };

        const onDrop = async (event) => {
            isDragOver.value = false;

            const files = event.dataTransfer?.files;
            if (!files || !files.length) return;

            const validImages = [];

            Array.from(files).forEach(file => {
                if (!file.type.startsWith('image/')) {
                    warningMessage.value = 'Warning: Non-image files were added. Only image files are allowed.';
                    return;
                }

                if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                    warningMessage.value = `Warning: File "${file.name}" exceeds ${MAX_SIZE_MB}MB and was skipped.`;
                    return;
                }

                validImages.push(file);
            });

            if (validImages.length) {
                const uploaded = await uploadImages(validImages);
                uploadedImages.value.push(...uploaded);
                warningMessage.value = '';
            }
        };

        const removeImage = (index) => {
            uploadedImages.value.splice(index, 1);
        };

        const truncateFileName = (name, maxLength) => {
            return name.length > maxLength ? name.substring(0, maxLength) + "..." : name;
        }

        const formatDate = (date) => {
            return date ? format(new Date(date), "MMMM d, yyyy") : "";
        };

        const isDateField = (fieldName) => {
            return fieldName.toLowerCase().includes("date");
        };

        const isDateOutOfRange = (date) => {
            const today = new Date();
            const orderDate = activeItem.value.order_delivery_date
                ? new Date(activeItem.value.order_delivery_date)
                : null;

            if (!orderDate) return date > today;
            return date < orderDate || date > today;
        };

        const disableFailureBeforeInstallation = (date) => {
            const installation = issueFormFields.value.installation_date
                ? new Date(issueFormFields.value.installation_date)
                : null;

            return (
                (installation && date < installation) ||
                isDateOutOfRange(date)
            );
        };

        const disableInstallationAfterFailure = (date) => {
            const failure = issueFormFields.value.failure_date
                ? new Date(issueFormFields.value.failure_date)
                : null;

            return (
                (failure && date > failure) ||
                isDateOutOfRange(date)
            );
        };

        const openModal = async () => {
            if (props.mainFormFields.quantity <= 0) {
                props.mainFormFields.quantity = '';
                await observer.value?.validate();
                isModalOpen.value = false;

                return;
            }

            isModalOpen.value = true;
        };

        const resetForm = () => {
            if (!props.mainFormFields.quantity) {
                observer.value?.reset();
            }
        };

        const validateNotExceedMainQuantity = async () => {
            const mainQty = Number(props.mainFormFields.quantity || 0);

            if (items.value.length === 0) {
                isQuantityInvalid.value = false;
                return;
            }

            const lowestQuantity = Math.min(...items.value.map(item => Number(item.quantity)));
            const isNotValid = lowestQuantity && mainQty < lowestQuantity;

            if (isNotValid) {
                await observer.value.setErrors({
                    quantity: 'Each issue quantity must not exceed the total quantity.'
                });
            }

            isQuantityInvalid.value = isNotValid;

            return isNotValid;
        };

        const validateIfIssuesDoesNotHaveHigherQuantity = () => {
            const mainQty = Number(props.mainFormFields.quantity || 0);

            if (items.value.length === 0) {
                isQuantityInvalid.value = false;
                return;
            }

            const isNotValid = items.value.some(item => Number(item.quantity) > mainQty);

            return isNotValid;
        };

        watch(() => props.orders[props.activeItemIndex]?.issues, (newIssues) => {
            items.value = newIssues ? [...newIssues] : [];
        }, { immediate: true });

        watch(() => props.activeItemIndex, () => {
            resetForm(); // or any other resets
            clearIssueForm(true);
        });

        onMounted( async () => {
            await fetchDropdownOptions();
        });

        return {
            dateFormat: computed(() => apiState.getLocale() == 'us' ? 'MM-DD-YYYY' : 'DD-MM-YYYY'),
            activeItem,
            items,
            columnsData,
            isModalOpen,
            issueFormFields,
            handleImageUpload,
            addIssue,
            currentItems,
            removeItem,
            issuesCategories,
            courseOfActions,
            setCourseOfAction,
            attributeInfo,
            saveMainForm,
            removeImage,
            uploadedImages,
            truncateFileName,
            formatDate,
            isDateField,
            onDragOver,
            onDragLeave,
            onDrop,
            isDragOver,
            warningMessage,
            editItem,
            isEditing,
            currentEditIndex,
            clearIssueForm,
            observer,
            issueObserver,
            openModal,
            disableFailureBeforeInstallation,
            disableInstallationAfterFailure,
            isQuantityInvalid,
            oldQuantity
        };
    },
});
</script>

<style lang="scss" scoped>
    .order-attributes {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        width: 100%;
        align-items: flex-start;

        @media (min-width: 1200px) {
            flex-wrap: nowrap;
        }

        div {
            width: 100%;
        }

        .attribute-tooltip {
            top: 4px;
            right: -2rem;
            padding-right: 16px;
            z-index: 10;
            max-width: 26px;
            cursor: pointer;
        }

        .attribute-tooltiptext {
            max-width: 360px !important;

            @media (min-width: 768px) {
                max-width: 600px !important;
            }
        }
    }

    .half-width {
        width: 44%;
        padding-right: 1rem;
    }
    .form__element {
        width: 100%;
    }

    ::v-deep .v-select .vs__selected {
        white-space: normal !important;
        line-height: 1.4;
    }

    ::v-deep .v-select .vs__dropdown-menu li {
        white-space: normal;
    }

    .input-row {
        width: 100%;
        gap: 20px;
        flex-wrap: wrap;

        & > div {
            width: 45%;
        }
    }

    .flex-row {
        flex-wrap: wrap;
        gap: 20px;
    }

    .flex-between {
        margin: 3px 0;
    }

    @media (min-width: 768px) {
        .input-row .form__element {
            width: calc(50% - 10px); /* 2 inputs per row with some space */
        }
    }

    .half-width {
        .form-group {
            width: 400px;

            @media screen and (max-width: 768px) {
                width: 100%;
            }
        }
    }

    .form__element {
        flex: 1; /* Makes inputs take equal width */
    }

    .table-container {
        width: 100%;
    }

    .vue-good-table {
        width: 100%;
    }

    .vgt-wrap  {
        width: 100%;
    }

    #add-to-cart-modal {
        .container {
            text-align: center;
        }

        .sf-loader {
            height: 440px;
        }

        p {
            font-size: 14px;
            color: var(--_c-dark-secondary);
        }

        .button:not(.small) {
            &.download {
                margin-top: 35px;
                margin-bottom: 0px;
                display: inline-block;
            }

            @include for-desktop() {
                font-size: 16px;
                line-height: 20px;
                padding: 8px 28px;
            }
        }

        .heading {
            max-width: 248px;

            button {
                min-width: 240px;
            }
        }
    }

    @include for-desktop() {
        .add-to-cart-modal {
            --modal-width: 29.375rem !important;
        }
    }

    .request-price {
        position: absolute;
        right: 15px;
        top: 15px;
        z-index: 1;
        padding: 5px 8px;

        &.disabled {
            border: none;
        }
    }
    .warning-message {
        color: var(--_c-red-primary);
        min-height: 30px;
    }

    .file-upload-label {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 12px;
        border: 2px dashed #ccc;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s;
        background-color: #fafafa;

        .hidden {
            width: 1px;
            height: 1px;
        }
    }

    .file-upload-label:hover {
        border-color: #007bff;
        background-color: #f0f8ff;
    }

    .upload-text {
        display: flex;
        align-items: center;
        text-align: center;
        gap: 8px;
        font-weight: 500;

        .svg-image {
            position: absolute;
        }
    }

    .upload-icon {
        font-size: 18px;
        color: #007bff;
    }

    .error-text {
        color: var(--c-danger);
        font-size: 14px;
        word-wrap: break-word;
        white-space: normal;
    }

    .attribute-tooltiptext {
        position: absolute;
        bottom: 30px;
        right: 0px;
        padding: 6px 10px;
        border-radius: 4px;
        display: none;
        background-color: var(--c-lighter-gray);
        border: 1px solid var(--c-light-gray);
        z-index: 1000;
    }

    .image-upload {
        .attribute-tooltiptext {
            bottom: 30px !important;
            right: 0px !important;
        }
    }

    .icon:hover + .attribute-tooltiptext {
        display: block;
    }

    /* File List */
    .file-list {
        margin-top: 10px;
        list-style: none;
        padding: 0;
    }

    .file-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 5px 10px;
        background: #f5f5f5;
        border-radius: 5px;
        margin-bottom: 5px;
        white-space: nowrap;
        overflow: hidden;
    }

    .file-name {
        max-width: 220px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .remove-btn {
        background: red;
        color: white;
        border: none;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        font-size: 12px;
        cursor: pointer;
    }

    .warning-message {
        font-size: 0.8rem;
        padding: 0;
        margin: 0;
        word-break: break-all;
    }

    .descrption {
        background: var(--c-lighter-gray);
        padding: 1rem;
        border-radius: 0.5rem;
    }

    ::v-deep .sf-textarea__label {
        font-size: 0.9rem;
        margin-bottom: 0.7rem;
    }
    ::v-deep .vs__selected-options {
        font-size: 0.9rem;
    }

    :v-deep #description {
        margin-top: 0.7rem;
    }

    ::v-deep .sf-textarea__textarea {
        width: 100% !important;
        box-sizing: border-box;
        margin-top: 0.7rem !important;
    }

    ::v-deep .sf-textarea > textarea {
        width: 100% !important;
    }

    ::v-deep .sf-input__error-message {
        max-width: 300px !important;

    }
</style>